/**
 * متنِ یک خبر، به زبانی که **همین حالا** روی اپ انتخاب شده.
 *
 * ═══ چرا لازم است ═══
 *
 * سطرِ `notifications` دو نسخه از یک متن دارد (مایگریشن ۰۱۱۲):
 *
 *   • `title`/`body` — متنی که سرور در لحظه‌ی ساختِ سطر نوشته، به زبانی که
 *     آن روز روی حسابِ گیرنده بوده. این همان است که به FCM می‌رود و چاره‌ای
 *     هم جز آن نیست: وقتی اپ بسته است هیچ کدی از ما روی گوشی اجرا نمی‌شود.
 *   • `titleKey`/`bodyKey` (+ جاگذارها) — همان متن به‌شکلِ کلیدِ دیکشنری.
 *
 * هرجا که **خودِ اپ** خبر را نشان می‌دهد باید از این‌جا برود، نه از ستونِ
 * متن. فرقشان دقیقاً همان باگی است که این را ساخت: کاربری که اپش انگلیسی
 * بود، خبرهای فارسیِ ماه‌ها پیش را روی نوارِ اعلانِ گوشی دید — چون اپ سطرِ
 * نخوانده را خودش دوباره نشان می‌دهد و متنِ ذخیره‌شده به زبانِ آن روز
 * قفل شده بود.
 *
 * ═══ همیشه چیزی برمی‌گرداند ═══
 *
 * دو مسیرِ عقب‌نشینی، هر دو به همان متنِ سرور:
 *
 *   ۱. سطر کلید ندارد — یا ترجمه‌پذیر نیست («نگار: قهوه‌ی عصر»)، یا از
 *      قبلِ ۰۱۱۲ مانده و مایگریشن نتوانسته کلیدش را پس بگیرد.
 *   ۲. دیکشنریِ اپ آن کلید را نمی‌شناسد — اپی که روی گوشی مانده و کلیدِ
 *      تازه‌ای که سرور می‌فرستد. `t()` در این حالت **خودِ نامِ کلید** را
 *      برمی‌گرداند، که روی نوارِ اعلان یعنی «notifTagEventBody». متنِ سرور
 *      به زبانِ دیگر، از این خیلی بهتر است.
 */
import { t } from './i18n.ts';
import type { AppNotification } from '../domain/types.ts';

type Textual = Pick<AppNotification,
  'title' | 'body' | 'titleKey' | 'titleVars' | 'bodyKey' | 'bodyVars'>;

/** ترجمه، مگر اینکه کلید نباشد یا دیکشنری نشناسدش (بالا را ببین). */
function render(
  key: string | null | undefined,
  vars: Record<string, string> | null | undefined,
  stored: string | null | undefined,
): string | null {
  if (!key) return stored ?? null;
  const out = t(key, vars ?? undefined);
  return out === key ? (stored ?? null) : out;
}

export const notifTitle = (n: Textual): string =>
  render(n.titleKey, n.titleVars, n.title) ?? n.title;

export const notifBody = (n: Textual): string | null =>
  render(n.bodyKey, n.bodyVars, n.body);
